import classNames from "classnames";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import DrawerToggle from "Shared/DrawerToggle";
import RoundedButton from "Shared/RoundedButton";
import CollapseIcon from "src/components/icons/Collapse";
import ExpandIcon from "src/components/icons/Expand";
import PoweredByImage from "src/images/powered-by.inline.svg";
import { AccountState, DealState } from "Types/state";
import "./GarageHeader.scss";
import GarageSettings from "./GarageSettings";
import { useLocation } from "react-router-dom";
import useEventListener from "src/utils/useEventListener";

export const TRANSLATION_KEY = "MyGarage.GarageHeader";

interface GarageHeaderProps {
    account: AccountState;
    className?: string;
    deal: DealState;
    open: boolean;
    style?: any;
}

// iOS 15 fix
// top: 62 = 20 px margin & 42 px for the button
const getPaymentSettingsButtonPosition = () => window.innerHeight - 62;

const GarageHeader = ({ account, className, open, deal, style }: GarageHeaderProps) => {
    const location = useLocation();
    const { t } = useTranslation();

    // Handles repositioning the payment settings button on mobile when the viewport changes
    const [paymentSettingsTop, setPaymentSettingsTop] = useState(() => getPaymentSettingsButtonPosition());
    const setTop = useCallback(() => setPaymentSettingsTop(getPaymentSettingsButtonPosition()), []);
    useEventListener("resize", setTop);

    return (
        <div className={classNames("GarageHeader", className)} style={style}>
            <div style={{ display: "flex", flexDirection: "column", flex: "auto", alignItems: "center" }}>
                <h1 className="GarageHeader__header">
                    {deal.person && deal.person.first_name
                        ? t(`${TRANSLATION_KEY}.myGarage`, { firstName: deal.person.first_name })
                        : t(`${TRANSLATION_KEY}.yourGarage`)}
                </h1>

                <div className="GarageHeader__buttons">
                    <div className="GarageHeader__buttonContainer">
                        <DrawerToggle
                            path={
                                open
                                    ? { pathname: "/my-garage", state: location.state }
                                    : { pathname: "/my-garage/payment-settings" }
                            }
                        >
                            <RoundedButton
                                className={classNames("MyGarage__gpcButton", {
                                    open,
                                })}
                                style={{ top: paymentSettingsTop }}
                            >
                                {open ? <CollapseIcon /> : <ExpandIcon />}{" "}
                                <span className="MyGarage__gpcButton--text">
                                    {open
                                        ? t(`${TRANSLATION_KEY}.backToGarageBtn`)
                                        : t(`${TRANSLATION_KEY}.paymentSettingsBtn`)}
                                </span>
                            </RoundedButton>
                        </DrawerToggle>
                    </div>
                    <div className="GarageHeader__buttonContainer">
                        <GarageSettings />
                    </div>
                </div>
            </div>
            <div className="GarageHeader__poweredBy">
                {account.logo_splash && (
                    <>
                        <img src={account.logo_splash} alt={account.name} className="GarageHeader__logo" />
                        <div className="GarageHeader__logo-separator" />
                    </>
                )}
                <PoweredByImage />
            </div>
        </div>
    );
};

export default GarageHeader;
